import React from "react";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftBadge from "components/SoftBadge";
import Card from "@mui/material/Card";

/**
 * SoftUIGroupTable
 *
 * Container-width responsive version:
 *  - reacts to the actual rendered card width, not viewport breakpoints
 *  - safer inside Grid item xl={4} / nested panels
 *  - horizontal scrolls instead of collapsing badly
 *  - auto-hides UUIDs on compact layouts
 *  - tighter spacing / smaller controls when container gets narrow
 */

function useElementWidth() {
  const ref = React.useRef(null);
  const [width, setWidth] = React.useState(0);

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;

    const update = () => {
      setWidth(el.getBoundingClientRect().width || 0);
    };

    update();

    const observer = new ResizeObserver(() => {
      update();
    });

    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  return [ref, width];
}

export default function SoftUIGroupTable({
  tree,
  metricsByUuid = {},
  columns,
  treeState,
  setTreeState,
  rootStateKey = "all",
  onTogglePath,
  height,
  sx,

  // responsive behaviour
  showUuid = "auto", // true | false | "auto"
  compactMetricWidth = 88,
  ultraCompactMetricWidth = 72,
  defaultMetricWidth = 120,
  minTableWidth,
  compactWidth = 900,
  ultraCompactWidth = 620,
}) {
  const [containerRef, containerWidth] = useElementWidth();

  const isCompact = containerWidth > 0 && containerWidth < compactWidth;
  const isUltraCompact = containerWidth > 0 && containerWidth < ultraCompactWidth;

  const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

  const shouldShowUuid = React.useMemo(() => {
    if (showUuid === true) return true;
    if (showUuid === false) return false;
    return !isCompact;
  }, [showUuid, isCompact]);

  const controlSize = isUltraCompact ? 28 : isCompact ? 30 : 34;
  const rowPy = isUltraCompact ? 0.85 : 1.1;
  const rowPx = isUltraCompact ? 1 : 1.5;
  const rowGap = isUltraCompact ? 0.75 : 1.25;
  const depthIndent = isUltraCompact ? 12 : 18;
  const valueVariant = isUltraCompact ? "caption" : "button";
  const labelVariant = isUltraCompact ? "body2" : "button";

  // ---- state lookup helpers (expand/visible)
  const getStateNodeForPath = React.useCallback(
    (path) => {
      const root = treeState?.[rootStateKey];
      if (!root) return null;

      let cur = root;
      for (const k of path) {
        cur = cur?.children?.[k];
        if (!cur) return null;
      }
      return cur;
    },
    [treeState, rootStateKey]
  );

  const getExpandForPath = React.useCallback(
    (path) => {
      const st = getStateNodeForPath(path);
      return !!st?.expand;
    },
    [getStateNodeForPath]
  );

  const getVisibleForPath = React.useCallback(
    (path) => {
      const st = getStateNodeForPath(path);
      if (!st) return true;
      return st.visible !== false;
    },
    [getStateNodeForPath]
  );

  // ---- default toggler if you didn't pass onTogglePath
  const defaultToggle = React.useCallback(
    (path) => {
      if (!setTreeState) return;

      const toggleRec = (node, p) => {
        if (!p.length) return { ...node, expand: !node.expand };
        const [head, ...rest] = p;
        const child = node?.children?.[head];
        if (!child) return node;
        return {
          ...node,
          children: {
            ...node.children,
            [head]: toggleRec(child, rest),
          },
        };
      };

      setTreeState((prev) => {
        const root = prev?.[rootStateKey];
        if (!root) return prev;
        return { ...prev, [rootStateKey]: toggleRec(root, path) };
      });
    },
    [setTreeState, rootStateKey]
  );

  const togglePath = onTogglePath || defaultToggle;

  // ---- visibility toggle
  const toggleVisiblePath = React.useCallback(
    (path) => {
      if (!setTreeState) return;

      const toggleRec = (node, p) => {
        if (!p.length) {
          const nextVisible = node?.visible === false ? true : false;
          return { ...node, visible: nextVisible };
        }
        const [head, ...rest] = p;
        const child = node?.children?.[head];
        if (!child) return node;
        return {
          ...node,
          children: {
            ...node.children,
            [head]: toggleRec(child, rest),
          },
        };
      };

      setTreeState((prev) => {
        const root = prev?.[rootStateKey];
        if (!root) return prev;
        return { ...prev, [rootStateKey]: toggleRec(root, path) };
      });
    },
    [setTreeState, rootStateKey]
  );

  // ---- metric aggregation
  const computeMetrics = React.useCallback(
    (node, path) => {
      if (!getVisibleForPath(path)) {
        return { in: 0, out: 0, net: 0 };
      }

      const isUuid = !!node?.uuid;
      if (isUuid) {
        const raw = metricsByUuid[node.uuid] || {};
        const _in = toNum(raw.in);
        const _out = toNum(raw.out);
        return { ...raw, in: _in, out: _out, net: _in - _out };
      }

      const kidsEntries = node?.children ? Object.entries(node.children) : [];
      const accum = {};

      for (const [childKey, childNode] of kidsEntries) {
        const childPath = [...path, childKey];
        const km = computeMetrics(childNode, childPath);

        for (const [mk, mv] of Object.entries(km || {})) {
          const num = Number(mv);
          if (!Number.isFinite(num)) continue;
          accum[mk] = (accum[mk] ?? 0) + num;
        }
      }

      const sumIn = toNum(accum.in);
      const sumOut = toNum(accum.out);
      accum.in = sumIn;
      accum.out = sumOut;
      if (!Number.isFinite(Number(accum.net))) accum.net = sumIn - sumOut;

      return accum;
    },
    [metricsByUuid, getVisibleForPath]
  );

  // ---- dynamic columns
  const defaultColumns = React.useMemo(
    () => [
      {
        key: "in",
        header: "In",
        width: defaultMetricWidth,
        compactWidth: compactMetricWidth,
        ultraCompactWidth: ultraCompactMetricWidth,
        align: "right",
        getValue: (row) => toNum(row.metrics?.in),
        format: (v) => Number(v).toLocaleString(),
        color: () => "dark",
      },
      {
        key: "out",
        header: "Out",
        width: defaultMetricWidth,
        compactWidth: compactMetricWidth,
        ultraCompactWidth: ultraCompactMetricWidth,
        align: "right",
        getValue: (row) => toNum(row.metrics?.out),
        format: (v) => Number(v).toLocaleString(),
        color: () => "dark",
      },
      {
        key: "net",
        header: "Net",
        width: defaultMetricWidth,
        compactWidth: compactMetricWidth,
        ultraCompactWidth: ultraCompactMetricWidth,
        align: "right",
        getValue: (row) => toNum(row.metrics?.net),
        format: (v) => Number(v).toLocaleString(),
        color: (v) => (v > 0 ? "success" : v < 0 ? "error" : "dark"),
      },
    ],
    [defaultMetricWidth, compactMetricWidth, ultraCompactMetricWidth]
  );

  const metricColumns = columns?.length ? columns : defaultColumns;

  const resolvedMetricColumns = React.useMemo(
    () =>
      metricColumns.map((c) => {
        const width = isUltraCompact
          ? c.ultraCompactWidth ?? ultraCompactMetricWidth
          : isCompact
            ? c.compactWidth ?? compactMetricWidth
            : c.width ?? defaultMetricWidth;

        return { ...c, _resolvedWidth: width };
      }),
    [metricColumns, isCompact, isUltraCompact, defaultMetricWidth, compactMetricWidth, ultraCompactMetricWidth]
  );

  const gridTemplateColumns = React.useMemo(() => {
    const widths = resolvedMetricColumns.map((c) => `${c._resolvedWidth}px`);
    return ["minmax(220px, 1fr)", ...widths].join(" ");
  }, [resolvedMetricColumns]);

  const tableMinWidth = React.useMemo(() => {
    if (minTableWidth) return minTableWidth;
    const metricTotal = resolvedMetricColumns.reduce((sum, c) => sum + c._resolvedWidth, 0);
    const firstColMin = isUltraCompact ? 220 : isCompact ? 260 : 320;
    return firstColMin + metricTotal;
  }, [minTableWidth, resolvedMetricColumns, isCompact, isUltraCompact]);

  const headerAlign = (align) =>
    align === "center" ? "center" : align === "left" ? "left" : "right";

  // ---- flatten rows
  const visibleRows = React.useMemo(() => {
    if (!tree) return [];

    const out = [];

    const walk = (node, nodeKey, path, depth) => {
      const isVisible = getVisibleForPath(path);
      const childrenObj = node?.children || {};
      const entries = Object.entries(childrenObj);
      const hasChildren = entries.length > 0;

      const metrics = computeMetrics(node, path);

      out.push({
        key: nodeKey,
        path,
        depth,
        label: node?.label ?? nodeKey,
        group: node?.group ?? "unknown",
        colour: node?.colour ?? "#CBD5E1",
        uuid: node?.uuid,
        hasChildren,
        metrics,
        isVisible,
      });

      if (!hasChildren) return;
      if (!isVisible) return;

      const isExpanded = getExpandForPath(path);
      if (!isExpanded) return;

      for (const [k, child] of entries) {
        walk(child, k, [...path, k], depth + 1);
      }
    };

    const rootHasChildren = tree?.children && Object.keys(tree.children).length > 0;
    const rootVisible = getVisibleForPath([]);

    out.push({
      key: rootStateKey,
      path: [],
      depth: 0,
      label: tree?.label ?? rootStateKey,
      group: tree?.group ?? "unknown",
      colour: tree?.colour ?? "#CBD5E1",
      uuid: tree?.uuid,
      hasChildren: !!rootHasChildren,
      metrics: computeMetrics(tree, []),
      isVisible: rootVisible,
    });

    if (rootHasChildren && rootVisible && getExpandForPath([])) {
      for (const [k, child] of Object.entries(tree.children)) {
        walk(child, k, [k], 1);
      }
    }

    return out;
  }, [tree, computeMetrics, getExpandForPath, getVisibleForPath, rootStateKey]);

  if (!tree) return null;

  return (
    <Card
      ref={containerRef}
      sx={{ height, display: "flex", flexDirection: "column", minWidth: 0, ...sx }}
    >
      <SoftBox
        px={isUltraCompact ? 1 : 2}
        py={2}
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          minWidth: 0,
          overflow: "auto",
        }}
      >
        <SoftBox sx={{ minWidth: tableMinWidth }}>
          {/* Header row */}
          <SoftBox
            display="grid"
            gridTemplateColumns={gridTemplateColumns}
            px={rowPx}
            py={isUltraCompact ? 1 : 1.25}
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 5,
              borderRadius: "12px",
              background: "rgba(248,249,250,0.9)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(233,236,239,0.9)",
            }}
          >
            <SoftTypography variant="caption" fontWeight="bold" color="dark" sx={{ minWidth: 0 }}>
              Group
            </SoftTypography>

            {resolvedMetricColumns.map((c) => (
              <SoftTypography
                key={c.key}
                variant="caption"
                fontWeight="bold"
                color="dark"
                textAlign={headerAlign(c.align)}
                sx={{
                  minWidth: 0,
                  fontSize: isUltraCompact ? "0.7rem" : undefined,
                }}
              >
                {c.header}
              </SoftTypography>
            ))}
          </SoftBox>

          {/* Rows */}
          <SoftBox mt={1}>
            {visibleRows.map((row) => {
              const isExpanded = row.hasChildren ? getExpandForPath(row.path) : false;
              const caret = row.hasChildren ? (isExpanded ? "▾" : "▸") : "•";

              return (
                <SoftBox
                  key={`${row.key}:${row.path.join("/") || "root"}`}
                  display="grid"
                  gridTemplateColumns={gridTemplateColumns}
                  alignItems="center"
                  px={rowPx}
                  py={rowPy}
                  mb={0.75}
                  sx={{
                    minWidth: 0,
                    borderRadius: "14px",
                    border: "1px solid rgba(233,236,239,0.85)",
                    background: "rgba(255,255,255,0.72)",
                    backdropFilter: "blur(10px)",
                    boxShadow: "0 10px 26px rgba(0,0,0,0.04)",
                    transition: "transform 0.12s ease, background 0.12s ease",
                    opacity: row.isVisible ? 1 : 0.45,
                    filter: row.isVisible ? "none" : "grayscale(0.15)",
                    "&:hover": {
                      background: "rgba(99, 99, 99, 0.06)",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  {/* Group cell */}
                  <SoftBox
                    display="flex"
                    alignItems="center"
                    gap={rowGap}
                    sx={{
                      pl: `${row.depth * depthIndent}px`,
                      minWidth: 0,
                      overflow: "hidden",
                    }}
                  >
                    <SoftBox
                      sx={{
                        width: "4px",
                        height: isUltraCompact ? "26px" : "34px",
                        borderRadius: "10px",
                        background: row.colour,
                        opacity: 0.95,
                        flex: "0 0 auto",
                      }}
                    />
                    <SoftBox
                      sx={{
                        width: isUltraCompact ? "8px" : "10px",
                        height: isUltraCompact ? "8px" : "10px",
                        borderRadius: "999px",
                        background: row.colour,
                        boxShadow: "0 0 0 3px rgba(255,255,255,0.9)",
                        border: "1px solid rgba(0,0,0,0.06)",
                        flex: "0 0 auto",
                      }}
                    />

                    {/* expand/collapse */}
                    <SoftButton
                      variant="outlined"
                      color="secondary"
                      size="small"
                      disabled={!row.hasChildren || !row.isVisible}
                      onClick={() => row.hasChildren && row.isVisible && togglePath(row.path)}
                      sx={{
                        minWidth: controlSize,
                        width: controlSize,
                        height: controlSize,
                        padding: 0,
                        borderRadius: "12px",
                        opacity: row.hasChildren ? 1 : 0,
                        fontSize: isUltraCompact ? "0.95rem" : "1.15rem",
                        cursor: row.hasChildren ? "pointer" : "default",
                        flex: "0 0 auto",
                      }}
                    >
                      {caret}
                    </SoftButton>

                    {/* show/hide */}
                    <SoftButton
                      variant="outlined"
                      color={row.isVisible ? "secondary" : "dark"}
                      size="small"
                      onClick={() => toggleVisiblePath(row.path)}
                      sx={{
                        minWidth: controlSize,
                        width: controlSize,
                        height: controlSize,
                        padding: 0,
                        fontSize: isUltraCompact ? "0.8rem" : "0.9rem",
                        borderRadius: "12px",
                        flex: "0 0 auto",
                      }}
                      title={row.isVisible ? "Hide this group" : "Show this group"}
                    >
                      {row.isVisible ? "👁" : "🚫"}
                    </SoftButton>

                    <SoftBox minWidth={0} flex={1} overflow="hidden">
                      <SoftTypography
                        variant={labelVariant}
                        fontWeight={row.depth === 0 ? "bold" : "medium"}
                        color="dark"
                        sx={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "block",
                          minWidth: 0,
                          fontSize: isUltraCompact ? "0.8rem" : undefined,
                          lineHeight: 1.15,
                        }}
                        title={row.label}
                      >
                        {row.label}
                      </SoftTypography>

                      <SoftBox
                        display="flex"
                        gap={0.75}
                        alignItems="center"
                        mt={0.5}
                        sx={{
                          minWidth: 0,
                          overflow: "hidden",
                          flexWrap: isUltraCompact ? "nowrap" : "wrap",
                        }}
                      >
                        <SoftBadge
                          variant="contained"
                          color={row.uuid ? "info" : "secondary"}
                          badgeContent={row.group}
                          container
                        />

                        {!row.isVisible && (
                          <SoftBadge variant="contained" color="dark" badgeContent="hidden" container />
                        )}

                        {shouldShowUuid && row.uuid && (
                          <SoftTypography
                            variant="caption"
                            color="text"
                            sx={{
                              fontFamily:
                                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: isCompact ? 180 : 380,
                              display: "block",
                              minWidth: 0,
                            }}
                            title={row.uuid}
                          >
                            {row.uuid}
                          </SoftTypography>
                        )}
                      </SoftBox>
                    </SoftBox>
                  </SoftBox>

                  {/* Metric columns */}
                  {resolvedMetricColumns.map((c) => {
                    const rawVal = c.getValue ? c.getValue(row, { metricsByUuid }) : undefined;
                    const content = c.format ? c.format(rawVal, row, { metricsByUuid }) : rawVal ?? "";
                    const colour = c.color ? c.color(rawVal, row, { metricsByUuid }) : "dark";

                    return (
                      <SoftTypography
                        key={`${row.path.join("/")}:${c.key}`}
                        variant={valueVariant}
                        fontWeight="bold"
                        color={colour}
                        textAlign={headerAlign(c.align)}
                        sx={{
                          fontVariantNumeric: "tabular-nums",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          minWidth: 0,
                          fontSize: isUltraCompact ? "0.78rem" : undefined,
                        }}
                        title={String(content ?? "")}
                      >
                        {content}
                      </SoftTypography>
                    );
                  })}
                </SoftBox>
              );
            })}
          </SoftBox>
        </SoftBox>
      </SoftBox>
    </Card>
  );
}